import { Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { FACTORYEXPORT_FREQUENCY } from "models";
import { ExportForm } from "modules/FactoryExportsDashboard/components/ExportModal/components";
import "modules/FactoryExportsDashboard/components/ExportModal/ExportModal.scss";
import { useAddExportMutation, useUpdateExportMutation } from "modules/FactoryExportsDashboard/components/ExportModal/hooks";
import { DEFAULT_EMAIL_MESSAGE, DEFAULT_EMAIL_SUBJECT } from "modules/FactoryExportsDashboard/utils";

import type { FactoryExport } from "models";
import type { FactoryExportFormPayload } from "modules/FactoryExportsDashboard/components/ExportModal/hooks";

type ExportModalProps = {
  factoryExport: FactoryExport | null;
  onClose: () => void;
};

const FormHelper = createFormHelper<FactoryExportFormPayload>();

const trimEmails = (emails: string) =>
  emails
    .split(",")
    .map(email => email.trim())
    .filter(Boolean)
    .join(",");

const isFormInvalid = ({ name, emails, factory_report_id, frequency, query_range, day, dealer_ids, dealer_location_ids }: FactoryExportFormPayload) =>
  !name.trim() ||
  !emails ||
  factory_report_id === null ||
  frequency === null ||
  query_range === null ||
  day === null ||
  (!dealer_ids?.length && !dealer_location_ids?.length);

export const ExportModal = ({ factoryExport, onClose }: ExportModalProps) => {
  const { mutateAsync: addExport, isPending: isAddExportPending } = useAddExportMutation();
  const { mutateAsync: updateExport, isPending: isUpdateExportPending } = useUpdateExportMutation();

  const handleSubmit = async (values: FactoryExportFormPayload) => {
    const { factory_report_id, frequency, query_range } = values;
    const day = frequency === FACTORYEXPORT_FREQUENCY.EveryDay ? 1 : values.day;

    if (isFormInvalid({ ...values, day }) || factory_report_id === null || frequency === null || query_range === null || day === null) return;

    const payload = { ...values, factory_report_id, frequency, query_range, day, emails: trimEmails(values.emails) };

    if (payload.id === null) await addExport(payload);
    else await updateExport(payload);

    onClose();
  };

  return (
    <Modal open size="large" className="ExportModal">
      <FormHelper.Form
        onSubmit={handleSubmit}
        defaultValues={{
          id: factoryExport?.id ?? null,
          name: factoryExport?.name ?? "",
          factory_report_id: factoryExport?.factory_report_id ?? null,
          emails: factoryExport?.emails ?? "",
          frequency: factoryExport?.frequency ?? null,
          day: factoryExport?.day ?? null,
          query_range: factoryExport?.query_range ?? null,
          is_ams_timezone: factoryExport?.is_ams_timezone ?? false,
          is_ods_format: factoryExport?.is_ods_format ?? false,
          dealer_ids: factoryExport?.dealer_ids ?? [],
          dealer_location_ids: factoryExport?.dealer_location_ids ?? [],
          brand_group_ids: factoryExport?.brand_group_ids ?? [],
          email_subject: factoryExport?.email_subject ?? DEFAULT_EMAIL_SUBJECT,
          email_message: factoryExport?.email_message ?? DEFAULT_EMAIL_MESSAGE
        }}
      >
        <ExportForm isEdit={factoryExport !== null} isPending={isAddExportPending || isUpdateExportPending} onClose={onClose} />
      </FormHelper.Form>
    </Modal>
  );
};
